import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, vModelText as _vModelText, withDirectives as _withDirectives, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, unref as _unref, createVNode as _createVNode, normalizeClass as _normalizeClass, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers, withKeys as _withKeys, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-6640837d"),n=n(),_popScopeId(),n)
const _hoisted_1 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", {
  class: "fixed inset-0 bg-gray-500/75",
  style: {"z-index":"9999"}
}, null, -1))
const _hoisted_2 = {
  class: "bg-white border border-gray-300 overflow-auto py-1",
  style: {"height":"300px"}
}
const _hoisted_3 = ["id", "onClick"]
const _hoisted_4 = { class: "flex gap-1 items-center flex-1 min-w-0" }
const _hoisted_5 = { class: "truncate" }
const _hoisted_6 = { class: "text-gray-400" }
const _hoisted_7 = { class: "text-gray-400" }
const _hoisted_8 = { class: "gap-1 items-center hidden lg:flex" }
const _hoisted_9 = {
  class: "text-gray-300 font-mono text-xs",
  style: {"width":"200px"}
}
const _hoisted_10 = {
  key: 0,
  class: "px-3 py-1.5 text-sm"
}
const _hoisted_11 = { class: "bg-white overflow-auto" }
const _hoisted_12 = { class: "text-sm px-3 py-1.5 font-semibold bg-gray-600 text-white truncate" }
const _hoisted_13 = ["onClick"]
const _hoisted_14 = { style: {"width":"40px"} }
const _hoisted_15 = { class: "truncate" }

import { Bars4Icon } from "@heroicons/vue/24/solid";
import { useHasPermission } from "@oculai/planner";
import { format } from "date-fns";
import FuzzySearch from "fuzzy-search";
import { computed, nextTick, onMounted, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import textService from "@/services/textService";
import { Project } from "@/types/Project";

type Page = {
  label: string;
  path: string;
  key: string;
};


export default /*#__PURE__*/_defineComponent({
  __name: 'QuickSearch',
  emits: ["closed"],
  setup(__props, { emit: __emit }) {

const backPage: Page = {
  label: "back",
  path: "back",
  key: "esc",
};

const searchValue = ref("");
const selectedProject = ref<Project | null>(null);
const searchInput = ref<HTMLInputElement | null>(null);
const pageContainerRef = ref<HTMLDivElement | null>(null);
const showPageSelector = ref(false);
const selectedPage = ref<Page | null>(null);

const store = useStore();
const router = useRouter();

const emit = __emit;

const normalizedProjects = computed(
  () =>
    store.state.projects.map((project: Project) => ({
      ...project,
      normalized_name: textService.normalize(project.name),
      normalized_customer_name: textService.normalize(project.customer_name),
      normalized_site_id: textService.normalize(project.site_id),
    })) as Project[],
);

const projects = computed(() => {
  if (!searchValue.value) {
    return normalizedProjects.value;
  }

  const searchItems = textService
    .normalize(searchValue.value)
    .toLowerCase()
    .split(" ")
    .map((item) => item.trim())
    .filter((item) => item);

  return searchItems.reduce((acc, search) => {
    const searcher = new FuzzySearch(
      acc,
      ["normalized_name", "normalized_customer_name", "normalized_site_id"],
      {
        caseSensitive: false,
        sort: false,
      },
    );
    return searcher.search(search);
  }, normalizedProjects.value);
});

const pages = computed(() => {
  if (!selectedProject.value) {
    return [];
  }
  const { customer_name, site_id } = selectedProject.value;
  return [
    {
      label: "Dashboard",
      path: "/dashboard",
      key: "b",
    },
    useHasPermission("daily_report_user", customer_name, site_id) && {
      label: "Daily Report",
      path: "/daily_report/overview",
      key: "d",
    },
    useHasPermission("planner_user", customer_name, site_id) && {
      label: "Planner",
      path: "/planner",
      key: "t",
    },
    useHasPermission("processes_user", customer_name, site_id) && {
      label: "Process Gantt",
      path: "/processes",
      key: "p",
    },
    useHasPermission("processes_user", customer_name, site_id) && {
      label: "Process Table",
      path: "/processes/table",
      key: "a",
    },
    useHasPermission("processes_user", customer_name, site_id) &&
      selectedProject.value.features.includes("working_hours") && {
        label: "Unit Values",
        path: "/unit_values_editor",
        key: "u",
      },
    useHasPermission("processes_user", customer_name, site_id) && {
      label: "Reports",
      path: "/reports",
      key: "r",
    },
    useHasPermission("site_activity_user", customer_name, site_id) && {
      label: "Site activity",
      path: "/labour/site_activity",
      key: "h",
    },
    useHasPermission("project_admin", customer_name, site_id) && {
      label: "Settings",
      path: "/settings",
      key: "s",
    },
    backPage,
  ].filter((item) => item) as Page[];
});

watch(
  () => projects.value,
  () => {
    selectProject(projects.value[0]);
  },
);

onMounted(() => {
  searchValue.value = "";
  setTimeout(() => {
    searchInput.value?.focus();
    selectProject(projects.value[0]);
  }, 50);
});

const selectProject = (project: Project) => {
  selectedProject.value = project;
  if (project) {
    document.getElementById(`quick-search-project-${project._id}`)?.scrollIntoView({
      block: "center",
    });
  }
};

const setShowPageSelector = (show: boolean) => {
  showPageSelector.value = show;
  if (show) {
    nextTick(() => {
      // this has to be in nextTick, otherwise useHasPermission doesn't work in the pages computed property
      selectedPage.value = pages.value[0];
      pageContainerRef.value?.focus();
    });
  } else {
    selectedPage.value = null;
    nextTick(() => {
      searchInput.value?.focus();
      // reselect project, so it's scrolled into view
      if (selectedProject.value) {
        selectProject(selectedProject.value);
      }
    });
  }
};

const handleProjectClick = (event: MouseEvent, project: Project) => {
  selectedProject.value = project;
  if (event.shiftKey) {
    setShowPageSelector(true);
  } else {
    goToSelectedProject(null);
  }
};

const goToProject = (project: Project, page: Page | null) => {
  const pagePath = page?.path || "/dashboard";
  router.push(`/${project.customer_name}/${project.site_id}${pagePath}`);
  emit("closed");
};

const moveProjectUp = () => {
  const project =
    (selectedProject.value &&
      projects.value[projects.value.findIndex((p) => p._id === selectedProject.value?._id) - 1]) ||
    projects.value[projects.value.length - 1];
  selectProject(project);
};

const moveProjectDown = () => {
  const project =
    (selectedProject.value &&
      projects.value[projects.value.findIndex((p) => p._id === selectedProject.value?._id) + 1]) ||
    projects.value[0];
  selectProject(project);
};

const goToSelectedProject = (page: Page | null) => {
  if (page?.path === backPage.path) {
    setShowPageSelector(false);
    return;
  }
  if (selectedProject.value) {
    goToProject(selectedProject.value, page);
  }
};

const movePageUp = () => {
  selectedPage.value =
    (selectedPage.value &&
      pages.value[pages.value.findIndex((p) => p.path === selectedPage.value?.path) - 1]) ||
    pages.value[pages.value.length - 1];
};

const movePageDown = () => {
  selectedPage.value =
    (selectedPage.value &&
      pages.value[pages.value.findIndex((p) => p.path === selectedPage.value?.path) + 1]) ||
    pages.value[0];
};

const handleInputKeyDown = (event: KeyboardEvent) => {
  const handledKeys = ["ArrowDown", "ArrowUp", "Enter"];
  if (handledKeys.includes(event.key)) {
    event.stopPropagation();
    event.preventDefault();

    if (event.key === "ArrowUp") {
      moveProjectUp();
    }
    if (event.key === "ArrowDown") {
      moveProjectDown();
    }
    if (event.key === "Enter") {
      if (event.shiftKey) {
        setShowPageSelector(true);
      } else {
        goToSelectedProject(null);
      }
    }

    return false;
  }
};

const handlePageKeyDown = (event: KeyboardEvent) => {
  const handledKeys = ["ArrowDown", "ArrowUp", "Enter"];
  if (handledKeys.includes(event.key)) {
    event.stopPropagation();
    event.preventDefault();

    if (event.key === "ArrowUp") {
      movePageUp();
    }
    if (event.key === "ArrowDown") {
      movePageDown();
    }
    if (event.key === "Enter") {
      goToSelectedProject(selectedPage.value);
    }
  } else {
    const shortcutPage = pages.value.find((page) => page.key === event.key);
    if (shortcutPage) {
      event.stopPropagation();
      event.preventDefault();

      goToSelectedProject(shortcutPage);
    }
  }
};

const handleEsc = () => {
  if (showPageSelector.value) {
    setShowPageSelector(false);
  } else {
    emit("closed");
  }
};

const handleOpenMenuClick = (event: Event, project: Project) => {
  event.stopPropagation();
  selectProject(project);
  setShowPageSelector(true);
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    onKeyup: _withKeys(handleEsc, ["esc"])
  }, [
    _hoisted_1,
    _createElementVNode("div", {
      class: "fixed inset-0 flex items-start lg:items-center justify-center",
      style: {"z-index":"10000"},
      onClick: _cache[3] || (_cache[3] = ($event: any) => (emit('closed')))
    }, [
      (!showPageSelector.value)
        ? (_openBlock(), _createElementBlock("div", {
            key: 0,
            class: "p-3 flex gap-1 flex-col text-gray-700 projectContainer",
            onClick: _cache[1] || (_cache[1] = _withModifiers(() => {}, ["stop"]))
          }, [
            _withDirectives(_createElementVNode("input", {
              ref_key: "searchInput",
              ref: searchInput,
              autofocus: "",
              autocomplete: "off",
              class: "px-3 py-2 w-full rounded border border-gray-300 focus:border-gray-300 outline-none placeholder-gray-300",
              placeholder: "search for project, customer name or site id...",
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((searchValue).value = $event)),
              onKeydown: handleInputKeyDown
            }, null, 544), [
              [_vModelText, searchValue.value]
            ]),
            _createElementVNode("div", _hoisted_2, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(projects.value, (project) => {
                return (_openBlock(), _createElementBlock("div", {
                  id: `quick-search-project-${project._id}`,
                  key: project._id,
                  class: _normalizeClass(["flex gap-1 cursor-pointer px-3 py-1.5 text-sm hover:bg-gray-200", selectedProject.value?._id === project._id ? ['bg-gray-100'] : []]),
                  onClick: ($event: any) => (handleProjectClick($event, project))
                }, [
                  _createElementVNode("div", _hoisted_4, [
                    _createElementVNode("div", _hoisted_5, _toDisplayString(project.name), 1),
                    _createElementVNode("div", _hoisted_6, _toDisplayString(project.customer_name), 1),
                    _createElementVNode("div", _hoisted_7, _toDisplayString(project.site_id), 1),
                    _createVNode(_unref(Bars4Icon), {
                      class: "text-gray-400 w-4 h-4 ml-2 cursor-context-menu",
                      onClick: ($event: any) => (handleOpenMenuClick($event, project))
                    }, null, 8, ["onClick"])
                  ]),
                  _createElementVNode("div", _hoisted_8, [
                    _createElementVNode("div", _hoisted_9, _toDisplayString(_unref(format)(project.planned_start, "dd.MM.yyyy")) + " - " + _toDisplayString(_unref(format)(project.planned_end, "dd.MM.yyyy")), 1),
                    _createElementVNode("div", {
                      class: _normalizeClass(["text-right", project.status === 'active' ? ['text-green-400'] : ['text-blue-400']]),
                      style: {"width":"80px"}
                    }, _toDisplayString(project.status), 3)
                  ])
                ], 10, _hoisted_3))
              }), 128)),
              (projects.value.length === 0)
                ? (_openBlock(), _createElementBlock("div", _hoisted_10, "No projects found"))
                : _createCommentVNode("", true)
            ])
          ]))
        : _createCommentVNode("", true),
      (showPageSelector.value && selectedProject.value)
        ? (_openBlock(), _createElementBlock("div", {
            key: 1,
            ref_key: "pageContainerRef",
            ref: pageContainerRef,
            class: "p-3 flex gap-1 flex-col text-gray-700 pageContainer select-none focus:outline-none",
            onClick: _cache[2] || (_cache[2] = _withModifiers(() => {}, ["stop"])),
            onKeydown: handlePageKeyDown,
            tabindex: -1
          }, [
            _createElementVNode("div", _hoisted_11, [
              _createElementVNode("div", _hoisted_12, _toDisplayString(selectedProject.value.customer_name) + "/" + _toDisplayString(selectedProject.value.site_id), 1),
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(pages.value, (page, index) => {
                return (_openBlock(), _createElementBlock("div", {
                  key: page.path,
                  class: _normalizeClass(["flex gap-1 cursor-pointer px-3 py-1.5 text-sm hover:bg-gray-200", {
              'bg-gray-100': selectedPage.value?.path === page.path,
              'border-t': index === pages.value.length - 1,
            }]),
                  onClick: ($event: any) => (goToSelectedProject(page))
                }, [
                  _createElementVNode("div", _hoisted_14, "[" + _toDisplayString(page.key) + "]", 1),
                  _createElementVNode("div", _hoisted_15, _toDisplayString(page.label), 1)
                ], 10, _hoisted_13))
              }), 128))
            ])
          ], 544))
        : _createCommentVNode("", true)
    ])
  ], 32))
}
}

})